<script setup lang="ts">
import { computed, ref } from 'vue';
import { getLabel } from '@/utils/data-display-utils';

import BaseQuestionLabel from '@/components/base/BaseQuestionLabel.vue';
import BaseQuestionChoice from '@/components/base/BaseQuestionChoice.vue';
import BaseQuestionOpen from '@/components/base/BaseQuestionOpen.vue';
import BaseQuestionLevel from '@/components/base/BaseQuestionLevel.vue';
import FormButtonContainer from '@/components/FormButtonContainer.vue';
import BaseCommentBox from '@/components/base/BaseCommentBox.vue';
import BaseQuestionRate from '@/components/base/BaseQuestionRate.vue';
import BaseQuestionMap from './BaseQuestionMap.vue';
import BaseImageResults from './BaseImageResults.vue';
import BaseRateResults from './BaseRateResults.vue';
import BaseQuestionDotVoting from './BaseQuestionDotVoting.vue';

import { getTranslation } from '@/utils/data-display-utils';
import { Answer, DotVotingStep, MapStep } from '@/store/form.Interface';
import { constant } from '@/constant';
import { Locales } from '@/store/language.interface';

// PROPS
const props = defineProps<{
  answers: Answer;
  formType: number;
  maxAnswers?: number;
  response: any;
  statistique: any;
  index: number;
  isUserAllowedToVote: boolean;
  iBelieveICareEnabled?: boolean;
  isFinalQuestion: boolean;
  showQuestionResults: boolean;
  areCommentsEnabled: boolean;
  mapMarkers: any[] | null;
  /** The participant's markers on a map form, as its cap counts them. */
  participantMarkerCount?: number;
  languageEnabled?: Locales[];
  sectionProhibitsResultsAndComments?: boolean;
  showAdminResults?: boolean;
}>();

// CONST
const State = constant.FormAnswer.FormState;

// REFS
const isVoting = ref(false);
const disagreeButtonClicked = ref(false);
const openQuestionAnswered = ref(false);
const voteSelected = ref([]);
const stepActive = ref(1);
const selectedResponses = ref([]);
const otherChoice = ref(null);
const questionOpenRef = ref<InstanceType<typeof BaseQuestionOpen>[]>([]);
const questionChoiceRef = ref<InstanceType<typeof BaseQuestionChoice>[]>([]);
const questionDotVotingRef = ref<InstanceType<typeof BaseQuestionDotVoting>[]>([]);

// EMITS
const emit = defineEmits<{
  (e: 'change', response: object): void;
  (e: 'nextForm', index: number): void;
  (e: 'submit', voteSelected: any): void;
  (e: 'voteNotAllowed'): void;
  (e: 'submitComment', userComment): void;
  (e: 'startComment'): void;
  (e: 'submitMarker', marker, answerId): void;
  (e: 'likeMarker', properties: Record<string, any>): void;
  (e: 'verifyUser'): void;
  (e: 'sliderSelected', response: object): void;
  (e: 'mapStart'): void;
}>();

// FUNCTIONS
function selectResponseChoices(choiceObject: any) {
  const maxAnswers = choiceObject.maxAnswers || 1;
  const response = choiceObject.response;
  if (!props.isUserAllowedToVote) {
    emit('voteNotAllowed');
  } else {
    const index = selectedResponses.value.indexOf(response);
    if (index === -1 && selectedResponses.value.length < maxAnswers) {
      selectedResponses.value.push(response);
    } else if (index === -1 && maxAnswers === 1) {
      selectedResponses.value = [response];
    } else if (index !== -1) {
      selectedResponses.value.splice(index, 1);
    }
    const step = props.answers.steps.find((s) => s.id === stepActive.value);
    const vsIndex = voteSelected.value.findIndex((v) => v.answerId === step.answerId);
    if (vsIndex === -1) {
      voteSelected.value.push({
        answerId: step.answerId,
        valueId: selectedResponses.value,
      });
    } else {
      if (selectedResponses.value.length === 0) {
        voteSelected.value.splice(vsIndex, 1);
      } else {
        voteSelected.value[vsIndex].valueId = selectedResponses.value;
      }
    }
    if (props.formType === constant.Form.Type.Proposal && response > 2) {
      disagreeButtonClicked.value = true;
    } else {
      disagreeButtonClicked.value = false;
      if (
        selectedResponses.value.length === maxAnswers &&
        props.formType === constant.Form.Type.Proposal &&
        props.iBelieveICareEnabled
      ) {
        stepActive.value = 2;
      }
    }
    isVoting.value = true;
  }
}

function selectResponseChoiceOther(choiceObject: any) {
  const maxAnswers = choiceObject.maxAnswers || 1;
  const response = choiceObject.response;
  if (!props.isUserAllowedToVote) {
    emit('voteNotAllowed');
  } else {
    otherChoice.value = response;
    const index = selectedResponses.value.indexOf('o');
    if (index === -1 && selectedResponses.value.length < maxAnswers) {
      selectedResponses.value.push('o');
    } else if (index === -1 && maxAnswers === 1) {
      selectedResponses.value = ['o'];
    } else if (index !== -1) {
      selectedResponses.value.splice(index, 1);
    }
    const step = props.answers.steps.find((s) => s.id === stepActive.value);
    const vsIndex = voteSelected.value.findIndex((v) => v.answerId === step.answerId);
    if (vsIndex === -1) {
      voteSelected.value.push({
        answerId: step.answerId,
        valueId: selectedResponses.value,
        valueText: otherChoice.value,
      });
    } else {
      voteSelected.value[vsIndex].valueText = otherChoice.value;
      if (voteSelected.value[vsIndex].valueId.indexOf('o') === -1) {
        voteSelected.value[vsIndex].valueId.push('o');
      }
    }
    isVoting.value = true;
  }
}

function otherChoiceReset() {
  const step = props.answers.steps.find((s) => s.id === stepActive.value);
  const index = selectedResponses.value.indexOf('o');
  if (index > -1) {
    selectedResponses.value.splice(index, 1);
  }
  const vsIndex = voteSelected.value.findIndex((v) => v.answerId === step.answerId);
  const otherOptionIndex = voteSelected.value[vsIndex]?.valueId.indexOf('o');
  if (otherOptionIndex > -1) {
    voteSelected.value[vsIndex].valueId.splice(otherOptionIndex, 1);
  }
  voteSelected.value[vsIndex].valueText = null;
}

function selectResponseLevels(response: { answerId: number; valueId: number }) {
  if (!props.isUserAllowedToVote) {
    emit('voteNotAllowed');
  } else {
    emit('sliderSelected', response);
  }

  const isAnswered = voteSelected.value.findIndex((vote) => vote.answerId === response.answerId);
  if (isAnswered === -1) {
    voteSelected.value.push({ answerId: response.answerId, valueId: [response.valueId] });
  } else {
    voteSelected.value[isAnswered] = { answerId: response.answerId, valueId: [response.valueId] };
  }
  isVoting.value = true;
}

function selectDotVotingResponses(responses: { answerId: number; valueId: number | null }[]) {
  if (!props.isUserAllowedToVote) {
    emit('voteNotAllowed');
  } else {
    const value = [];
    for (const response of responses) {
      value.push({ answerId: response.answerId, valueId: response.valueId ? [response.valueId] : [] });
    }
    voteSelected.value = value;
    isVoting.value = true;
  }
}

function responseOpen(response: any) {
  if (!props.isUserAllowedToVote) {
    emit('voteNotAllowed');
  } else {
    const hasAnswers = response.valueText.length > 0;
    if (hasAnswers) {
      voteSelected.value[0] = response;
    } else {
      voteSelected.value = [];
    }
    openQuestionAnswered.value = hasAnswers;
    isVoting.value = true;
  }
}

function catchScrollAction(index: number) {
  emit('nextForm', index);
}

function resetResponse() {
  stepActive.value = 1;
  selectedResponses.value = [];
  voteSelected.value = [];
  isVoting.value = true;
  disagreeButtonClicked.value = false;
  openQuestionAnswered.value = false;
}

function undoCancelVote() {
  stepActive.value = 1;
  selectedResponses.value = [];
  voteSelected.value = [];
  isVoting.value = false;
  disagreeButtonClicked.value = false;
  openQuestionAnswered.value = false;
  switch (props.formType) {
    case constant.Form.Type.DotVoting:
      questionDotVotingRef.value?.forEach((x) => x.resetValue());
      break;
    case constant.Form.Type.OpenQuestion:
      questionOpenRef.value?.forEach((x) => x.resetValue());
      break;
    case constant.Form.Type.Question:
      questionChoiceRef.value?.forEach((x) => x.resetOtherChoiceValue());
      break;
  }
}

function submit() {
  isVoting.value = false;
  emit('submit', voteSelected.value);
}

function submitComment(userComment) {
  emit('submitComment', userComment);
}

function startComment() {
  emit('startComment');
}

function submitMarker(marker) {
  emit('submitMarker', marker, props.answers.steps[0].answerId);
}

function likeMarker(properties: Record<string, any>) {
  emit('likeMarker', properties);
}

function verifyUser() {
  emit('voteNotAllowed');
}

function mapStart() {
  emit('mapStart');
}

// COMPUTED
const activeScreen = computed(() => `step-${stepActive.value}`);

const showResult = computed(() => props.response.length > 0);

const showSubmit = computed(() => {
  const stepCount = props.answers && props.answers.steps.length;
  return (
    (voteSelected.value.length > 0 && activeScreen.value === `step-${stepCount}`) ||
    disagreeButtonClicked.value ||
    openQuestionAnswered.value
  );
});

const hasVotes = computed(() => props.response[0]?.votes ?? null);

const isImageQuestion = computed(() => !!props.answers.steps[0].choices[0].image);

const hasAnswers = computed(() => {
  switch (props.formType) {
    case constant.Form.Type.Proposal:
      return voteSelected.value.length > 0 && voteSelected.value.every((x) => x.valueId.length > 0);
    case constant.Form.Type.Question:
      return (
        selectedResponses.value.length > 0 &&
        (!selectedResponses.value.includes('o') ||
          voteSelected.value.filter((x) => x.valueId.includes('o') && x.valueText.length > 0).length > 0)
      );
    case constant.Form.Type.Level:
      return voteSelected.value.length > 0 && voteSelected.value.every((x) => x.valueId.length > 0);
    case constant.Form.Type.OpenQuestion:
      return voteSelected.value.length > 0 && voteSelected.value[0].valueText.length > 0;
    case constant.Form.Type.DotVoting:
      const step = props.answers.steps.find((s) => s.id === stepActive.value) as DotVotingStep;
      return (
        voteSelected.value.length > 0 &&
        (step.dot_voting_type === constant.Form.DotVoting.Type.Fixed ||
          voteSelected.value.every((x) => x.valueId.length > 0))
      );
    default:
      return false;
  }
});

const state = computed(() => {
  if (props.showAdminResults) {
    return State.ResultWithStats;
  } else if (props.showQuestionResults) {
    return props.sectionProhibitsResultsAndComments
      ? hasVotes.value && !isVoting.value
        ? State.ResultWithoutStats
        : State.NoContent
      : State.ResultWithStats;
  } else if (hasVotes.value && !isVoting.value) {
    return props.sectionProhibitsResultsAndComments ? State.ResultWithoutStats : State.ResultWithStats;
  }
  return State.Question;
});
</script>
<template>
  <div class="base-question">
    <div v-if="props.answers.steps" v-for="step in props.answers.steps">
      <div v-if="props.formType === constant.Form.Type.Proposal">
        <BaseQuestionChoice
          v-if="step.type === 'choices' && activeScreen === `step-${step.id}` && state === State.Question"
          :choices="step.choices.sort((a, b) => a.order_num - b.order_num)"
          :maxAnswers="step.maxAnswers"
          :selected-responses="selectedResponses"
          :other-choice="step.otherChoice"
          @buttonChoiceClick="selectResponseChoices($event)"
          @otherChoiceSet="selectResponseChoiceOther($event)"
          @otherChoiceReset="otherChoiceReset"
          :other-choice-label="step.otherChoice ? getTranslation(step, 'otherChoiceLabel', props.languageEnabled) : ''"
          :language-enabled="props.languageEnabled"
        />
        <BaseQuestionLevel
          v-if="step.type === 'levels' && activeScreen === `step-${step.id}` && state === State.Question"
          :levels="step.levels"
          @change="selectResponseLevels($event)"
          :language-enabled="props.languageEnabled"
        />
        <!-- shows other accord/desaccord results -->
        <BaseQuestionLabel
          v-if="step.type === 'choices' && [State.ResultWithStats, State.ResultWithoutStats].includes(state)"
          :choices="step.choices"
          :other-choice="step.otherChoice"
          :response="response && response[0]?.votes.filter((r) => r.answer_id === step.id)"
          :statistiques="statistique?.step?.[step.id]"
          :other-choice-label="step.otherChoice ? getTranslation(step, 'otherChoiceLabel', props.languageEnabled) : ''"
          :language-enabled="props.languageEnabled"
          :show-stats="state === State.ResultWithStats"
        />
        <!-- shows j'ycrois j'y tiens results -->
        <BaseQuestionLevel
          v-if="step.type === 'levels' && [State.ResultWithStats, State.ResultWithoutStats].includes(state)"
          :levels="step.levels"
          :statistiques="statistique"
          :show-result="[State.ResultWithStats, State.ResultWithoutStats].includes(state)"
          :language-enabled="props.languageEnabled"
          :response="response"
          :show-stats="state === State.ResultWithStats"
        />
      </div>
      <div v-if="props.formType === constant.Form.Type.Question">
        <BaseQuestionChoice
          ref="questionChoiceRef"
          v-if="step.type === 'choices' && activeScreen === `step-${step.id}` && state === State.Question"
          :choices="step.choices.sort((a, b) => a.order_num - b.order_num)"
          :maxAnswers="step.maxAnswers"
          :selected-responses="selectedResponses"
          :other-choice="step.otherChoice"
          @buttonChoiceClick="selectResponseChoices($event)"
          @otherChoiceSet="selectResponseChoiceOther($event)"
          @otherChoiceReset="otherChoiceReset"
          :other-choice-label="step.otherChoice ? getTranslation(step, 'otherChoiceLabel', props.languageEnabled) : ''"
          :language-enabled="props.languageEnabled"
        />
        <BaseImageResults
          v-if="
            step.type === 'choices' &&
            isImageQuestion &&
            [State.ResultWithStats, State.ResultWithoutStats].includes(state)
          "
          :choices="step.choices"
          :other-choice="step.otherChoice"
          :response="response && response[0]?.votes.filter((r) => r.answer_id === step.id)"
          :statistiques="statistique?.step?.[step.id]"
          :other-choice-label="step.otherChoice ? getTranslation(step, 'otherChoiceLabel', props.languageEnabled) : ''"
          :show-stats="state === State.ResultWithStats"
          :language-enabled="props.languageEnabled"
        />
        <BaseQuestionLabel
          v-if="
            step.type === 'choices' &&
            !isImageQuestion &&
            [State.ResultWithStats, State.ResultWithoutStats].includes(state)
          "
          :choices="step.choices"
          :other-choice="step.otherChoice"
          :response="response && response[0]?.votes.filter((r) => r.answer_id === step.id)"
          :statistiques="statistique?.step?.[step.id]"
          :other-choice-label="step.otherChoice ? getTranslation(step, 'otherChoiceLabel', props.languageEnabled) : ''"
          :show-stats="state === State.ResultWithStats"
          :language-enabled="props.languageEnabled"
        />
      </div>
      <div v-if="props.formType === constant.Form.Type.OpenQuestion">
        <base-question-open
          ref="questionOpenRef"
          v-if="step.type === 'open' && activeScreen === `step-${step.id}`"
          :open-question="step"
          @change="responseOpen($event)"
          :response="response[0]?.votes.filter((r) => r.answer_id === step.id)"
          :show-result="[State.ResultWithStats, State.ResultWithoutStats, State.NoContent].includes(state)"
          :language-enabled="props.languageEnabled"
        />
      </div>
      <div
        v-if="props.formType === constant.Form.Type.Level"
        v-for="slider in [...step.levels].sort((a, b) => a.order_num - b.order_num)"
        :key="slider.answerId"
      >
        <BaseQuestionRate
          v-if="step.type === 'levels' && state === State.Question"
          :levels="slider"
          :choices="step.level_choices"
          :statistiques="statistique"
          :selected-response="hasVotes ? hasVotes : selectedResponses"
          :results="hasVotes"
          :vote-selected="voteSelected"
          :language-enabled="props.languageEnabled"
          :section-prohibits-results-and-comments="props.sectionProhibitsResultsAndComments"
          @change="selectResponseLevels($event)"
        />
        <BaseRateResults
          v-else-if="[State.ResultWithStats, State.ResultWithoutStats].includes(state)"
          :statistiques="statistique?.step?.[slider.answerId]?.[0]?.percentage || 0"
          :slider="slider"
          :labels="step.level_choices"
          :results="hasVotes && hasVotes.find((result) => result.answer_id === slider.answerId)"
          :language-enabled="props.languageEnabled"
          :show-stats="state === State.ResultWithStats"
        />
      </div>
      <div v-if="props.formType === constant.Form.Type.Map">
        <BaseQuestionMap
          :id="`base-question-${props.index}`"
          :mapConfig="(step as MapStep).map"
          :mapMarkers="mapMarkers"
          :participant-marker-count="props.participantMarkerCount"
          :show-question-results="showQuestionResults"
          :language-enabled="props.languageEnabled"
          @submit-marker="submitMarker"
          @like-marker="likeMarker"
          @verify-user="verifyUser"
          @map-start="mapStart"
        />
      </div>
      <div v-if="props.formType === constant.Form.Type.DotVoting">
        <BaseQuestionDotVoting
          ref="questionDotVotingRef"
          :step="step"
          :language-enabled="props.languageEnabled"
          :statistics="props.statistique"
          :user-vote="props.response"
          :state="state"
          @update:model-value="selectDotVotingResponses"
        />
      </div>
    </div>
    <div class="base-question__comment" v-if="showResult && !isVoting && !showQuestionResults && areCommentsEnabled">
      <div class="base-question__comment-title">{{ getLabel('form.comment-title', props.languageEnabled) }}</div>
      <base-comment-box
        :language-enabled="props.languageEnabled"
        @submit-comment="submitComment($event)"
        @start-comment="startComment"
      />
    </div>
    <form-button-container
      v-if="props.formType != constant.Form.Type.Map"
      :index="index"
      :is-voting="isVoting"
      :show-result="showResult"
      :show-submit="showSubmit"
      :is-final-question="props.isFinalQuestion"
      :has-answers="hasAnswers"
      @submit="submit"
      @reset-response="resetResponse"
      @next-form="catchScrollAction(index)"
      @undo-cancel-vote="undoCancelVote"
      :show-question-results="showQuestionResults"
      :language-enabled="props.languageEnabled"
    />
  </div>
</template>

<style lang="scss">
.base-question {
  display: flex;
  flex-direction: column;
  margin-top: $space-md;

  &__slider {
    display: flex;
    flex-direction: column;

    &-wrapper {
      width: 100%;
    }

    .q-slider {
      width: 50%;
    }
  }

  &-answers {
    width: 100%;

    .q-btn {
      margin-top: 5px;
      margin-bottom: 5px;
      width: 100%;
    }
  }

  &__comment {
    margin: $space-md 0 $space-sm 0;
    display: flex;
    flex-direction: column;
    gap: $space-xs;

    &-title {
      font-size: $body-text;
    }
  }

  .base-question {
    &__poll-labels {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      margin-top: 5px;

      .q-btn {
        padding: 0;
      }

      .q-linear-progress {
        height: 100%;
      }

      .slider-results {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        width: 100%;
        margin: 0px 5px;
        font-size: $small-text;
      }
    }

    &__poll-results {
      display: flex;
      flex-direction: row;
      justify-content: space-between;

      &-container {
        padding: 0px 5px;
        width: 100%;
      }
    }
  }
}
</style>
